import {
  getGlobalStats,
  getMedalHiscores,
  getMedalScoreThresholdsForMap,
} from "@dailywars/core/db";
import { buildMapId } from "@dailywars/core/engine";
import { createServerFn } from "@tanstack/react-start";

import { getLatestUpdate } from "#/content/updates";
import { getRequestLogger, setRouteAction } from "#/lib/requestLogger.server";
import { addUtcDays } from "#/lib/yearActivityGrid";
import { HERO_LEADERBOARD_FETCH_COUNT } from "#/routes/-play.labels";
import { isEarlyBirdPromoAvailable } from "#/server/billing/earlyBirdPromoAvailability.server";
import { CALENDAR_START_DATE, toUtcDateString } from "#/server/game/dailyMapIds";
import { gameContextMiddleware } from "#/server/game/gameContextMiddleware";
import { shapeGlobalStats } from "#/server/game/globalStats";
import { buildAccessByMapId } from "#/server/game/playIndexAccess";

import { getMapBestScore, getMapTopScores } from "./readHandlers";
import type { RequestGameContext } from "./requestGameContext";

const MONTHLY_HISCORE_LEADERS_COUNT = 10;

async function loadPlayIndexBoardData({
  todayStr,
  todayStandardId,
  todayPremiumId,
  accessContext,
}: {
  todayStr: string;
  todayStandardId: string;
  todayPremiumId: string;
  accessContext: { playerId: string; entitlements: RequestGameContext["entitlements"] };
}) {
  const [
    standardBestScore,
    premiumBestScore,
    standardLeaderboard,
    premiumLeaderboard,
    standardMedalThresholds,
    premiumMedalThresholds,
  ] = await Promise.all([
    getMapBestScore({ mapId: todayStandardId, context: accessContext }),
    getMapBestScore({ mapId: todayPremiumId, context: accessContext }),
    getMapTopScores({ mapId: todayStandardId, limit: HERO_LEADERBOARD_FETCH_COUNT }),
    getMapTopScores({ mapId: todayPremiumId, limit: HERO_LEADERBOARD_FETCH_COUNT }),

    getMedalScoreThresholdsForMap({ mapId: todayStandardId, mapDate: todayStr }),
    getMedalScoreThresholdsForMap({ mapId: todayPremiumId, mapDate: todayStr }),
  ]);

  return {
    standardBestDays: standardBestScore.bestDays,
    premiumBestDays: premiumBestScore.bestDays,
    standardLeaderboard: standardLeaderboard.entries,
    premiumLeaderboard: premiumLeaderboard.entries,
    standardMedalThresholds,
    premiumMedalThresholds,
  };
}

async function loadYesterdayLeaderboards(yesterdayStr: string) {
  if (yesterdayStr < CALENDAR_START_DATE) {
    return null;
  }

  const yesterdayStandardId = buildMapId(yesterdayStr, "standard");
  const yesterdayPremiumId = buildMapId(yesterdayStr, "premium");
  const [standardLeaderboard, premiumLeaderboard] = await Promise.all([
    getMapTopScores({ mapId: yesterdayStandardId, limit: HERO_LEADERBOARD_FETCH_COUNT }),
    getMapTopScores({ mapId: yesterdayPremiumId, limit: HERO_LEADERBOARD_FETCH_COUNT }),
  ]);

  return {
    yesterdayStandardMapId: yesterdayStandardId,
    yesterdayPremiumMapId: yesterdayPremiumId,
    yesterdayStandardLeaderboard: standardLeaderboard.entries,
    yesterdayPremiumLeaderboard: premiumLeaderboard.entries,
  };
}

async function buildPlayIndexData(context: RequestGameContext) {
  const todayStr = toUtcDateString(new Date());
  const yesterdayStr = addUtcDays(todayStr, -1);
  const monthKey = todayStr.slice(0, 7);
  const todayStandardId = buildMapId(todayStr, "standard");
  const todayPremiumId = buildMapId(todayStr, "premium");
  const todayMapIds = [todayStandardId, todayPremiumId];
  const accessContext = {
    playerId: context.playerId,
    entitlements: context.entitlements,
  };

  const [board, yesterdayBoards, earlyBirdAvailable, globalStatsRecord, monthlyHiscores] =
    await Promise.all([
      loadPlayIndexBoardData({
        todayStr,
        todayStandardId,
        todayPremiumId,
        accessContext,
      }),
      loadYesterdayLeaderboards(yesterdayStr),
      isEarlyBirdPromoAvailable({ log: getRequestLogger() }),
      getGlobalStats(),
      getMedalHiscores({ period: monthKey, limit: MONTHLY_HISCORE_LEADERS_COUNT }),
    ]);

  const latestUpdate = getLatestUpdate();

  return {
    standardMapId: todayStandardId,
    premiumMapId: todayPremiumId,
    todayStr,
    monthKey,
    ...board,
    yesterdayBoards,
    monthlyHiscoreLeaders: monthlyHiscores.entries,
    standardDailyStreak: context.standardDailyStreak,
    premiumDailyStreak: context.premiumDailyStreak,
    accessByMapId: buildAccessByMapId({ mapIds: todayMapIds, entitlements: context.entitlements }),
    showEarlyBirdPromo: context.entitlements.tier !== "premium" && earlyBirdAvailable,
    globalStats: shapeGlobalStats(globalStatsRecord),
    isAnonymous: context.isAnonymous,
    lastSeenUpdateId: context.isAnonymous ? null : (context.player?.lastSeenUpdateId ?? null),
    latestUpdate: latestUpdate
      ? {
          id: latestUpdate.id,
          title: latestUpdate.title,
        }
      : null,
  };
}

export const loadPlayIndexData = createServerFn({ method: "GET" })
  .middleware([gameContextMiddleware])
  .handler(async ({ context }) => {
    setRouteAction("play_index");
    return buildPlayIndexData(context.gameContext);
  });
